
import { memo, useCallback, useState } from "react";

import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { MasonryItems } from "../../data/data";

/* =========================================
   ESTILOS ESTÁTICOS
========================================= */

const gridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    md: "repeat(4, minmax(0, 1fr))",
  },
  gridAutoRows: {
    xs: "100px",
    sm: "120px",
    md: "140px",
  },
  gridAutoFlow: "dense",
  gap: { xs: 0.75, sm: 1 },
  width: "100%",
};

const imageSx = {
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const modalPaperSx = {
  bgcolor: "#111",
  borderRadius: 2,
  overflow: "hidden",
};

const modalContentSx = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: { xs: 5, sm: 6 },
  minHeight: 250,
  bgcolor: "#111",
};

const modalImageSx = {
  display: "block",
  maxWidth: "100%",
  maxHeight: "75vh",
  width: "auto",
  height: "auto",
  objectFit: "contain",
};

const iconButtonSx = {
  color: "#fff",
  bgcolor: "rgba(0,0,0,0.55)",
  "&:hover": {
    bgcolor: "rgba(80,80,80,0.85)",
  },
};

/* =========================================
   TARJETA MEMORIZADA
========================================= */

const MasonryItem = memo(function MasonryItem({
  item,
  index,
  onOpen,
}) {
  const handleClick = useCallback(() => {
    onOpen(index);
  }, [onOpen, index]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen(index);
      }
    },
    [onOpen, index]
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Ampliar imagen ${item.id}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        gridRow:
          item.height === "lg"
            ? "span 3"
            : item.height === "med"
              ? "span 2"
              : "span 1",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: "action.hover",
        contain: "layout paint",
        contentVisibility: "auto",
        containIntrinsicSize: "auto 200px",
        "&:focus-visible": {
          outline: "3px solid",
          outlineColor: "primary.main",
          outlineOffset: -3,
        },
      }}
    >
      <Box
        component="img"
        src={item.imagen}
        alt={`Imagen ${item.id}`}
        loading={index === 0 ? "eager" : "lazy"}
        fetchPriority={index === 0 ? "high" : "auto"}
        decoding="async"
        draggable={false}
        sx={imageSx}
      />
    </Box>
  );
});

/* =========================================
   CUADRÍCULA MEMORIZADA
========================================= */

const MasonryGrid = memo(function MasonryGrid({ onOpen }) {
  return (
    <Box sx={gridSx}>
      {MasonryItems.map((item, index) => (
        <MasonryItem
          key={item.id}
          item={item}
          index={index}
          onOpen={onOpen}
        />
      ))}
    </Box>
  );
});

/* =========================================
   MODAL INDEPENDIENTE
========================================= */

const MasonryModal = memo(function MasonryModal({
  index,
  onClose,
  onChange,
}) {
  const open = index !== null;
  const item = open ? MasonryItems[index] : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      keepMounted={false}
      slotProps={{
        paper: {
          sx: modalPaperSx,
        },
      }}
    >
      {item && (
        <DialogContent sx={modalContentSx}>
          <Box
            component="img"
            src={item.imagen}
            alt={`Imagen ${item.id}`}
            decoding="async"
            sx={modalImageSx}
          />

          <IconButton
            onClick={onClose}
            aria-label="Cerrar modal"
            sx={{
              ...iconButtonSx,
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={() => onChange(-1)}
            aria-label="Imagen anterior"
            sx={{
              ...iconButtonSx,
              position: "absolute",
              left: { xs: 4, sm: 12 },
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <IconButton
            onClick={() => onChange(1)}
            aria-label="Imagen siguiente"
            sx={{
              ...iconButtonSx,
              position: "absolute",
              right: { xs: 4, sm: 12 },
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>

          <Typography
            sx={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              color: "#fff",
              fontSize: 14,
            }}
          >
            {index + 1} / {MasonryItems.length}
          </Typography>
        </DialogContent>
      )}
    </Dialog>
  );
});

/* =========================================
   COMPONENTE PRINCIPAL
========================================= */

function Masonry() {
  const [imagenActiva, setImagenActiva] = useState(null);

  const abrirImagen = useCallback((index) => {
    setImagenActiva(index);
  }, []);

  const cerrarModal = useCallback(() => {
    setImagenActiva(null);
  }, []);

  const cambiarImagen = useCallback((direccion) => {
    setImagenActiva((actual) => {
      if (actual === null) return null;

      return (
        actual + direccion + MasonryItems.length
      ) % MasonryItems.length;
    });
  }, []);

  return (
    <>
      <MasonryGrid onOpen={abrirImagen} />

      <MasonryModal
        index={imagenActiva}
        onClose={cerrarModal}
        onChange={cambiarImagen}
      />
    </>
  );
}

export default memo(Masonry);
